import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import type { TLessonBase } from '@onesource/schemas';
import { LessonProgressStatus } from '@onesource/schemas';
import { useGetProgress } from '@onesource/services';
import { getProgressDisplay } from './progress-display';

interface SubLessonCardProps {
  lesson: TLessonBase;
  index: number;
  onClick: () => void;
}

export const SubLessonCard = ({ lesson, index, onClick }: SubLessonCardProps) => {
  const { data: progress } = useGetProgress(lesson.id);
  const progressDisplay = getProgressDisplay(progress || undefined);

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '15px',
        backgroundColor: 'white',
        '&:hover': {
          boxShadow: '0px 8px 16px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
          borderColor: 'primary.light',
        },
      }}
      elevation={1}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '0.9rem',
              flexShrink: 0,
            }}
          >
            {index + 1}
          </Box>
          <Box flex={1}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="h6" sx={{ flex: 1, fontWeight: 600, fontSize: '1rem' }}>
                {lesson.title}
              </Typography>
              <Chip
                label={progressDisplay.label}
                size="small"
                color={progressDisplay.color}
                variant={progressDisplay.variant === 'filled' ? 'filled' : 'outlined'}
                icon={progressDisplay.icon}
                sx={{ ml: 2 }}
              />
            </Box>
            {lesson.shortDescription && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {lesson.shortDescription}
              </Typography>
            )}
            {progress && progress.status === LessonProgressStatus.IN_PROGRESS && (
              <Box mt={1.5}>
                <LinearProgress
                  variant="determinate"
                  value={progress.progressPercent ?? 50}
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
